import { CurrencyCode } from 'further-types/lp/vehicle';
import { Row } from 'further-ui/components/Layout';
import Table from 'further-ui/components/Table';
import { numberToCurrencyString } from 'further-ui/utils';
import { sumBy } from 'lodash';

type Props = {
  investorsBreakdown: Array<{
    investorName: string;
    commitmentId: string;
    investments: number;
    expenses: number;
    fees: number;
    workingCapital: number;
    distributions: number;
    distributionsOutsideOfCarry: number;
    returnOfCapital: number;
    equalisations: number;
    netCallOrDistribution: number;
    capitalContributionToCall: number;
    capitalContributionUsed: number;
    totalToCall: number;
  }>;
  baseCurrency?: CurrencyCode;
};

const InvestorBreakdown: React.FC<Props> = ({
  baseCurrency,
  investorsBreakdown,
}) => {
  const hasInvestments = sumBy(investorsBreakdown, 'investments') !== 0;
  const hasExpenses = sumBy(investorsBreakdown, 'expenses') !== 0;
  const hasFees = sumBy(investorsBreakdown, 'fees') !== 0;
  const hasWorkingCapital = sumBy(investorsBreakdown, 'workingCapital') !== 0;
  const hasEqualisations = sumBy(investorsBreakdown, 'equalisations') !== 0;
  const hasDistributions = sumBy(investorsBreakdown, 'distributions') !== 0;
  const hasDistributionsOutsideOfCarry =
    sumBy(investorsBreakdown, 'distributionsOutsideOfCarry') !== 0;
  const hasReturnOfCapital = sumBy(investorsBreakdown, 'returnOfCapital') !== 0;
  const hasCapitalContributionUsed =
    sumBy(investorsBreakdown, 'capitalContributionUsed') !== 0;
  const hasCapitalContributionToCall =
    sumBy(investorsBreakdown, 'capitalContributionToCall') !== 0;

  const total = sumBy(investorsBreakdown, 'totalToCall');

  const rows = investorsBreakdown.map((entry) => ({
    ...entry,
    id: entry.commitmentId,
  }));

  const columns = [
    {
      key: 'investorName',
      label: 'Investor',
    },
    ...(hasInvestments
      ? [
          {
            key: 'investments',
            label: 'Call - Investments',
            render: (row: (typeof rows)[number]) =>
              numberToCurrencyString(row.investments, {
                currency: baseCurrency,
              }),
          },
        ]
      : []),
    ...(hasExpenses
      ? [
          {
            key: 'expenses',
            label: 'Call - Expenses',
            render: (row: (typeof rows)[number]) =>
              numberToCurrencyString(row.expenses, { currency: baseCurrency }),
          },
        ]
      : []),
    ...(hasFees
      ? [
          {
            key: 'fees',
            label: 'Call - Management fees',
            render: (row: (typeof rows)[number]) =>
              numberToCurrencyString(row.fees, { currency: baseCurrency }),
          },
        ]
      : []),
    ...(hasWorkingCapital
      ? [
          {
            key: 'workingCapital',
            label: 'Call - General capital',
            render: (row: (typeof rows)[number]) =>
              numberToCurrencyString(row.workingCapital, {
                currency: baseCurrency,
              }),
          },
        ]
      : []),
    ...(hasEqualisations
      ? [
          {
            key: 'equalisations',
            label: 'Equalisation call/(distribution)',
            render: (row: (typeof rows)[number]) =>
              numberToCurrencyString(row.equalisations, {
                currency: baseCurrency,
                negativeInBrackets: true,
              }),
          },
        ]
      : []),
    ...(hasDistributions
      ? [
          {
            key: 'distributions',
            label: 'Distributions',
            render: (row: (typeof rows)[number]) =>
              numberToCurrencyString(row.distributions, {
                currency: baseCurrency,
              }),
          },
        ]
      : []),
    ...(hasDistributionsOutsideOfCarry
      ? [
          {
            key: 'distributionsOutsideOfCarry',
            label: 'Distributions outside of carry',
            render: (row: (typeof rows)[number]) =>
              numberToCurrencyString(row.distributionsOutsideOfCarry, {
                currency: baseCurrency,
              }),
          },
        ]
      : []),
    ...(hasReturnOfCapital
      ? [
          {
            key: 'returnOfCapital',
            label: 'Return of capital',
            render: (row: (typeof rows)[number]) =>
              numberToCurrencyString(row.returnOfCapital, {
                currency: baseCurrency,
              }),
          },
        ]
      : []),
    {
      key: 'netCallOrDistribution',
      label: 'Net call/(distribution)',
      render: (row: (typeof rows)[number]) =>
        numberToCurrencyString(row.netCallOrDistribution, {
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
    ...(hasCapitalContributionToCall
      ? [
          {
            key: 'capitalContributionToCall',
            label: 'Capital contribution to call',
            render: (row: (typeof rows)[number]) =>
              numberToCurrencyString(row.capitalContributionToCall, {
                currency: baseCurrency,
              }),
          },
        ]
      : []),
    ...(hasCapitalContributionUsed
      ? [
          {
            key: 'capitalContributionUsed',
            label: 'Capital contribution used',
            render: (row: (typeof rows)[number]) =>
              numberToCurrencyString(row.capitalContributionUsed, {
                currency: baseCurrency,
                negativeInBrackets: true,
              }),
          },
          {
            key: 'totalToCall',
            label: 'Total to call',
            render: (row: (typeof rows)[number]) =>
              numberToCurrencyString(row.totalToCall, {
                currency: baseCurrency,
                negativeInBrackets: true,
              }),
          },
        ]
      : []),
  ];

  const footer = [
    {
      key: 'investorsNumberTotal',
      content: `Investors: ${investorsBreakdown.length}`,
      colspan: columns.length > 3 ? columns.length - 3 : 1,
    },
    {
      key: 'label',
      content: <Row justify="flex-end">Total capital call/(distribution)</Row>,
      colspan: columns.length > 3 ? 2 : 1,
    },
    {
      key: 'total',
      content: numberToCurrencyString(total, {
        currency: baseCurrency,
        negativeInBrackets: true,
      }),
    },
  ];

  return (
    <Table
      title="Investor breakdown"
      columns={columns}
      rows={rows}
      customFooterRow={footer}
    />
  );
};

export default InvestorBreakdown;
